// ── Clear Series mutation hook (FR-SESS-002 — "KEEP + DETACH") ───────────────
// POST /cohorts/:cohort_id/sessions/clear is NOW LIVE. clear-series is granted to
// super_admin + coach-in-scope only (migration 0227); the page renders the action
// for those roles and the backend re-applies the in-scope guard (a foreign cohort
// 404s-via-400). The mutation requires an explicit destructive { confirm: true }
// body and a generated UUID Idempotency-Key, so a replay produces NO second effect.
// The honest cleared/detached envelope is returned VERBATIM (counts only; the
// invites/recordings/reports are KEPT — never deleted). On success we invalidate
// the session reads so the cleared future sessions drop out of the active series.

import { useMutation, useQueryClient } from "@tanstack/react-query";
import { getCoachingReadinessServices } from "../services/coaching-service-provider";
import type { CoachingClearSeriesRequest } from "../services/coaching-readiness-dtos";
import { coachingQueryKeys } from "./coaching-query-keys";
import { createIdempotencyKey } from "./useCoachingQueries";

export function useClearSeriesMutation(cohortId: string | null | undefined) {
  const queryClient = useQueryClient();
  return useMutation({
    // The confirm flag is supplied by the caller's explicit destructive confirm;
    // a fresh UUID Idempotency-Key is minted per submission (idempotent on replay).
    mutationFn: (body: CoachingClearSeriesRequest) =>
      getCoachingReadinessServices().sessions.clearSeries(
        cohortId as string,
        body,
        createIdempotencyKey(`clear-series:${cohortId ?? "unknown"}`)
      ),
    onSuccess: async () => {
      // The cleared future sessions leave the active series — refresh the session
      // list (cohort-scoped + global) so the UI reflects the SERVER state.
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: coachingQueryKeys.list("sessions.list") }),
        queryClient.invalidateQueries({ queryKey: coachingQueryKeys.list("sessions.listForCohort") })
      ]);
    }
  });
}
